/** Sets a Partner Portal password from an invitation or reset link, or replaces a temporary one. */
import { useState } from "react";
import { Link, useSearchParams } from "react-router";
import { AuthAlert, AuthShell } from "../kit/Auth";
import { Bar, Button, PasswordInput } from "../kit/ui";
import { PARTNER_HELP, PARTNER_SCENE } from "./SignIn";

export function Setup() {
  const [sp] = useSearchParams();
  const token = sp.get("token") ?? "";
  const change = sp.get("change") === "1";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const strong = pw.length >= 12;
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const res = await fetch("/auth/setup", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ token: token || undefined, password: pw }) });
    const j = await res.json().catch(() => ({}));
    setBusy(false);
    if (res.ok) {
      if (change) return window.location.assign("/");
      try {
        localStorage.setItem("inlinea.msp_id", j.msp_id);
      } catch {
        /* private mode */
      }
      setDone(`${j.email} (MSP ID ${j.msp_id})`);
    }
    else setError(j.message ?? "That did not work. Ask for a new link.");
  };
  return (
    <AuthShell product="Partner Portal" scene={PARTNER_SCENE} help={PARTNER_HELP}>
      {done ? (
        <>
          <h1 className="auth-title">Password set</h1>
          <p className="auth-lead">Sign in as {done}. Then add your authenticator app.</p>
          <div className="auth-form">
            <Link className="btn primary xl" to="/signin" style={{ textDecoration: "none" }}>Sign in</Link>
          </div>
        </>
      ) : (
        <>
          <h1 className="auth-title">Choose your password</h1>
          <p className="auth-lead">At least 12 characters. An authenticator app is added at your first sign-in.</p>
          <form className="auth-form" onSubmit={submit}>
            {error && <AuthAlert>{error}</AuthAlert>}
            {!token && !change && <AuthAlert>This page needs the link from your invitation email.</AuthAlert>}
            {change && <AuthAlert tone="amber">You signed in with a temporary password. Choose your own to continue.</AuthAlert>}
            <div className="field">
              <label htmlFor="p1">Password</label>
              <PasswordInput id="p1" autoComplete="new-password" value={pw} onChange={setPw} autoFocus />
              <Bar parts={[{ value: Math.min(pw.length, 12), tone: strong ? "ok" : "am" }]} total={12} height={4} />
              <span className="hint">{strong ? "Long enough." : `${12 - pw.length} more ${12 - pw.length === 1 ? "character" : "characters"}.`}</span>
            </div>
            <div className="field">
              <label htmlFor="p2">Type it again</label>
              <PasswordInput id="p2" autoComplete="new-password" value={pw2} onChange={setPw2} />
              {pw2 && pw2 !== pw && <span className="err">The two don't match.</span>}
            </div>
            <Button type="submit" kind="primary" className="xl" busy={busy} disabled={(!token && !change) || !strong || pw !== pw2}>Set password</Button>
          </form>
        </>
      )}
    </AuthShell>
  );
}
